import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

interface Audience {
  id?: number;
  client_id?: number;
  name: string;
  description: string;
  age_min: number | null;
  age_max: number | null;
  gender: string;
  locations: string | null;
  languages: string | null;
  job_titles: string | null;
  job_functions: string | null;
  seniorities: string | null;
  industries: string | null;
  company_sizes: string | null;
  interests: string | null;
  behaviors: string | null;
  custom_audiences: string | null;
  lookalike_audiences: string | null;
  platform: string;
  is_active: boolean;
}

interface AudienceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  audience?: Audience | null;
  onSuccess: () => void;
}

interface TagsInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

function TagsInput({ value, onChange, placeholder }: TagsInputProps) {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed && !value.includes(trimmed)) {
        onChange([...value, trimmed]);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
      {value.map((tag, index) => (
        <span
          key={index}
          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm"
        >
          {tag}
          <button
            type="button"
            onClick={() => removeTag(index)}
            className="ml-1 text-blue-600 hover:text-blue-800"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[200px] outline-none"
      />
    </div>
  );
}

export default function AudienceFormModal({
  isOpen,
  onClose,
  clientId,
  audience,
  onSuccess,
}: AudienceFormModalProps) {
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

  const [formData, setFormData] = useState<Audience>({
    name: '',
    description: '',
    age_min: null,
    age_max: null,
    gender: 'all',
    locations: null,
    languages: null,
    job_titles: null,
    job_functions: null,
    seniorities: null,
    industries: null,
    company_sizes: null,
    interests: null,
    behaviors: null,
    custom_audiences: null,
    lookalike_audiences: null,
    platform: 'all',
    is_active: true,
  });

  const [expandedSections, setExpandedSections] = useState({
    demographics: true,
    geographic: true,
    professional: false,
    interests: false,
    custom: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (audience) {
      setFormData({
        ...audience,
        id: audience.id,
      });

      // Expand relevant sections based on platform
      if (audience.platform === 'linkedin') {
        setExpandedSections(prev => ({ ...prev, professional: true }));
      }
      if (audience.platform === 'facebook' || audience.platform === 'instagram') {
        setExpandedSections(prev => ({ ...prev, interests: true }));
      }
    } else {
      // Reset form for new audience
      setFormData({
        name: '',
        description: '',
        age_min: null,
        age_max: null,
        gender: 'all',
        locations: null,
        languages: null,
        job_titles: null,
        job_functions: null,
        seniorities: null,
        industries: null,
        company_sizes: null,
        interests: null,
        behaviors: null,
        custom_audiences: null,
        lookalike_audiences: null,
        platform: 'all',
        is_active: true,
      });
    }
  }, [audience, isOpen]);

  const parseJsonField = (field: string | null): string[] => {
    if (!field) return [];
    try {
      return JSON.parse(field);
    } catch {
      return [];
    }
  };

  const handleArrayChange = (field: keyof Audience, values: string[]) => {
    setFormData({
      ...formData,
      [field]: values.length > 0 ? JSON.stringify(values) : null,
    });
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        client_id: clientId,
      };

      if (audience?.id) {
        // Update existing
        await axios.put(`${API_BASE_URL}/audiences/${audience.id}`, payload);
      } else {
        // Create new
        await axios.post(`${API_BASE_URL}/audiences`, payload);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Failed to save audience:', err);
      setError(err.response?.data?.error || 'Failed to save audience');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const showProfessional = formData.platform === 'linkedin' || formData.platform === 'all';
  const showInterests = formData.platform === 'facebook' || formData.platform === 'instagram' || formData.platform === 'all';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {audience?.id ? 'Edit Audience' : 'Create New Audience'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic Info - Always Visible */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Audience Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Tech Professionals 25-45"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this audience..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Platform *</label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Platforms</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="google">Google Ads</option>
                <option value="display">Display Network</option>
              </select>
            </div>
          </div>

          {/* Demographics Section */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => toggleSection('demographics')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">Demographics</span>
              {expandedSections.demographics ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.demographics && (
              <div className="p-4 border-t border-gray-200 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Age
                    </label>
                    <input
                      type="number"
                      min="13"
                      max="100"
                      value={formData.age_min || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, age_min: e.target.value ? parseInt(e.target.value) : null })
                      }
                      placeholder="18"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Age
                    </label>
                    <input
                      type="number"
                      min="13"
                      max="100"
                      value={formData.age_max || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, age_max: e.target.value ? parseInt(e.target.value) : null })
                      }
                      placeholder="65"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Geographic Section */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => toggleSection('geographic')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">Geographic Targeting</span>
              {expandedSections.geographic ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.geographic && (
              <div className="p-4 border-t border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Locations
                  </label>
                  <TagsInput
                    value={parseJsonField(formData.locations)}
                    onChange={(tags) => handleArrayChange('locations', tags)}
                    placeholder="Add locations (e.g., United States, New York)"
                  />
                  <p className="mt-1 text-xs text-gray-500">Press Enter or comma to add</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Languages
                  </label>
                  <TagsInput
                    value={parseJsonField(formData.languages)}
                    onChange={(tags) => handleArrayChange('languages', tags)}
                    placeholder="Add languages (e.g., English, Spanish)"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Professional Targeting Section (LinkedIn) */}
          {showProfessional && (
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleSection('professional')}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <span className="font-medium text-gray-900">
                  Professional Targeting (LinkedIn)
                </span>
                {expandedSections.professional ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {expandedSections.professional && (
                <div className="p-4 border-t border-gray-200 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Titles
                    </label>
                    <TagsInput
                      value={parseJsonField(formData.job_titles)}
                      onChange={(tags) => handleArrayChange('job_titles', tags)}
                      placeholder="e.g., CEO, CTO, Marketing Director"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Functions
                    </label>
                    <TagsInput
                      value={parseJsonField(formData.job_functions)}
                      onChange={(tags) => handleArrayChange('job_functions', tags)}
                      placeholder="e.g., Engineering, Marketing"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Seniority Levels
                    </label>
                    <TagsInput
                      value={parseJsonField(formData.seniorities)}
                      onChange={(tags) => handleArrayChange('seniorities', tags)}
                      placeholder="e.g., Director, VP, CXO"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industries
                    </label>
                    <TagsInput
                      value={parseJsonField(formData.industries)}
                      onChange={(tags) => handleArrayChange('industries', tags)}
                      placeholder="e.g., Technology, Healthcare"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Sizes
                    </label>
                    <TagsInput
                      value={parseJsonField(formData.company_sizes)}
                      onChange={(tags) => handleArrayChange('company_sizes', tags)}
                      placeholder="e.g., 1-10, 11-50, 51-200"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Interests & Behaviors Section (Facebook/Instagram) */}
          {showInterests && (
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleSection('interests')}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <span className="font-medium text-gray-900">
                  Interests & Behaviors (Facebook/Instagram)
                </span>
                {expandedSections.interests ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {expandedSections.interests && (
                <div className="p-4 border-t border-gray-200 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interests
                    </label>
                    <TagsInput
                      value={parseJsonField(formData.interests)}
                      onChange={(tags) => handleArrayChange('interests', tags)}
                      placeholder="e.g., Technology, Marketing, Travel"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Behaviors
                    </label>
                    <TagsInput
                      value={parseJsonField(formData.behaviors)}
                      onChange={(tags) => handleArrayChange('behaviors', tags)}
                      placeholder="e.g., Online Shopping, Business Travel"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Custom Audiences Section */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => toggleSection('custom')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">Custom Audiences</span>
              {expandedSections.custom ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedSections.custom && (
              <div className="p-4 border-t border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Audience IDs
                  </label>
                  <TagsInput
                    value={parseJsonField(formData.custom_audiences)}
                    onChange={(tags) => handleArrayChange('custom_audiences', tags)}
                    placeholder="Enter custom audience IDs"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lookalike Audience IDs
                  </label>
                  <TagsInput
                    value={parseJsonField(formData.lookalike_audiences)}
                    onChange={(tags) => handleArrayChange('lookalike_audiences', tags)}
                    placeholder="Enter lookalike audience IDs"
                  />
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Saving...' : audience?.id ? 'Update Audience' : 'Create Audience'}
          </button>
        </div>
      </div>
    </div>
  );
}